'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './ProjectBoard.module.scss';
import {
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/* ── Inline SVG icons ── */
const SvgGrip = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <circle cx="9" cy="5" r="1" />
    <circle cx="9" cy="12" r="1" />
    <circle cx="9" cy="19" r="1" />
    <circle cx="15" cy="5" r="1" />
    <circle cx="15" cy="12" r="1" />
    <circle cx="15" cy="19" r="1" />
  </svg>
);

const SvgBranch = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="6" y1="3" x2="6" y2="15" />
    <circle cx="18" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <path d="M18 9v1a3 3 0 0 1-3 3H9a3 3 0 0 0-3 3v1" />
  </svg>
);

const SvgTicket = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 9a3 3 0 0 1 0-6h20a3 3 0 0 1 0 6" />
    <path d="M2 15a3 3 0 0 0 0 6h20a3 3 0 0 0 0-6" />
    <line x1="6" y1="12" x2="6.01" y2="12" />
    <line x1="10" y1="12" x2="10.01" y2="12" />
  </svg>
);

const SvgServer = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="8" rx="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const SvgFlag = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const SvgCheck = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const SvgGithub = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

const SvgCode = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const SvgDatabase = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const SvgUsers = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const SvgTarget = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const SvgPalette = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="13.5" cy="6.5" r=".5" />
    <circle cx="17.5" cy="10.5" r=".5" />
    <circle cx="8.5" cy="7.5" r=".5" />
    <circle cx="6.5" cy="12.5" r=".5" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.01 17.461 2 12 2z" />
  </svg>
);

const SvgBug = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m8 2 1.9 2.4" />
    <path d="M14.1 4.4 16 2" />
    <path d="M9 7h6" />
    <path d="M8 7a4 4 0 0 0-4 4v3a6 6 0 0 0 12 0v-3a4 4 0 0 0-4-4" />
    <path d="M3 13H1" />
    <path d="M23 13h-2" />
    <path d="M4 19 2 21" />
    <path d="M20 19 22 21" />
    <path d="M12 11v7" />
  </svg>
);

/* ── Types ── */
type MetaType = 'branch' | 'jira' | 'env' | 'featureFlag' | 'check';
type TagType = 'Web' | 'Data' | 'Agile' | 'Produit' | 'Design' | 'Security';

interface TicketData {
  id: string;
  title: string;
  tag: TagType;
  col: string;
  meta?: { type: MetaType; label: string };
}

interface ColumnDef {
  id: string;
  label: string;
}

interface TravelerState {
  ticket: TicketData;
  fromLeft: number;
  fromTop: number;
  toLeft: number;
  toTop: number;
}

interface RoleStyle {
  color: string;
  soft: string;
  icon: React.ReactNode;
}

interface TestingMetric {
  key: string;
  label: string;
  value: string;
  sub: string;
  tone: 'good' | 'warning' | 'neutral';
  icon: React.ReactNode;
}

interface QualityToolDetail {
  label: string;
  value: string;
}

interface DashboardMetric {
  key: 'dependabot' | 'codeguard' | 'sonar';
  label: string;
  headline: string;
  summary: string;
  status: 'ok' | 'warning' | 'info';
  details: QualityToolDetail[];
}

type IncidentPhase = 'idle' | 'detected' | 'repairing' | 'resolved';

/* ── Constants ── */
const COLUMNS: ColumnDef[] = [
  { id: 'building', label: 'Building' },
  { id: 'review', label: 'Review' },
  { id: 'testing', label: 'Testing' },
];

const EMPTY_MESSAGES: Record<string, string> = {
  building:
    'Aucune tâche en construction pour l’instant. Chez Averiane, une colonne vide n’est pas un trou dans le suivi : elle montre que le flux avance vite, ou que nous concentrons l’équipe sur une autre priorité avant d’enchainer.',
  review:
    'Rien en review pour le moment. Nous validons chaque livrable avant qu’il ne poursuive son chemin, avec une traçabilité claire sur les blocages et les arbitrages techniques.',
  testing:
    'Aucun ticket en testing. La qualité est vérifiée au fil de l’eau, sans file d’attente cachée ni étape noyée dans un pipeline opaque.',
};

const ROLE_STYLES: Record<TagType, RoleStyle> = {
  Web: { color: '#2563eb', soft: 'rgba(37,99,235,0.08)', icon: <SvgCode /> },
  Data: { color: '#b98745', soft: 'rgba(185,135,69,0.10)', icon: <SvgDatabase /> },
  Agile: { color: '#10b981', soft: 'rgba(16,185,129,0.08)', icon: <SvgUsers /> },
  Produit: { color: '#1e3a5f', soft: 'rgba(30,58,95,0.07)', icon: <SvgTarget /> },
  Design: { color: '#f59e0b', soft: 'rgba(245,158,11,0.08)', icon: <SvgPalette /> },
  Security: { color: '#dc2626', soft: 'rgba(220,38,38,0.10)', icon: <SvgBug /> },
};

const SECURITY_TICKET_ID = 'security-hotfix';
const SECURITY_TICKET: TicketData = {
  id: SECURITY_TICKET_ID,
  title: 'Bug sécurité détecté',
  tag: 'Security',
  col: 'review',
  meta: { type: 'jira', label: 'SEC-041' },
};

const INITIAL_TICKETS: TicketData[] = [
  { id: 't1', title: 'Architecture Data Pipeline', tag: 'Data', col: 'building' },
  {
    id: 't2',
    title: 'Dashboard analytics v2',
    tag: 'Data',
    col: 'building',
    meta: { type: 'branch', label: 'feat/analytics-v2' },
  },
  {
    id: 't3',
    title: 'API GraphQL & Auth',
    tag: 'Web',
    col: 'building',
    meta: { type: 'branch', label: 'feat/graphql-auth' },
  },
  {
    id: 't4',
    title: 'CI/CD & Déploiement',
    tag: 'Web',
    col: 'review',
    meta: { type: 'jira', label: 'HEST-128' },
  },
  { id: 't5', title: 'Sprint Planning & Rétro', tag: 'Agile', col: 'review' },
  { id: 't6', title: 'Roadmap Q3 & Priorisation', tag: 'Produit', col: 'building' },
  {
    id: 't8',
    title: 'Design System & Maquettes',
    tag: 'Design',
    col: 'review',
    meta: { type: 'jira', label: 'HEST-112' },
  },
  {
    id: 't9',
    title: 'Prototypage interactif',
    tag: 'Design',
    col: 'testing',
    meta: { type: 'check', label: 'Livré le 4 juin' },
  },
  { id: 't11', title: 'Modèle ML prédictif', tag: 'Data', col: 'building' },
];

const TOTAL_TICKETS = INITIAL_TICKETS.length;
const INITIAL_TESTED_IDS = INITIAL_TICKETS.filter((ticket) => ticket.col === 'testing').map(
  (ticket) => ticket.id
);
const INITIAL_TESTED_COUNT = INITIAL_TESTED_IDS.length;

const META_ICON: Record<MetaType, React.ReactNode> = {
  branch: <SvgBranch />,
  jira: <SvgTicket />,
  env: <SvgServer />,
  featureFlag: <SvgFlag />,
  check: <SvgCheck />,
};

/* ── Metric icons ── */
const SvgShield = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const SvgPercent = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="19" y1="5" x2="5" y2="19" />
    <circle cx="6.5" cy="6.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);

const SvgTicketStack = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 9a3 3 0 0 1 0-6h20a3 3 0 0 1 0 6" />
    <path d="M2 15a3 3 0 0 0 0 6h20a3 3 0 0 0 0-6" />
    <line x1="6" y1="12" x2="6.01" y2="12" />
    <line x1="10" y1="12" x2="10.01" y2="12" />
  </svg>
);

const SvgCheckCircle = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const SvgClock = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const SvgGitBranch = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="6" y1="3" x2="6" y2="15" />
    <circle cx="18" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <path d="M18 9v1a3 3 0 0 1-3 3H9a3 3 0 0 0-3 3v1" />
  </svg>
);

const SvgBox = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const SvgCloud = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
  </svg>
);

/* ── Helpers ── */
const getTicketRelativePos = (ticketId: string, boardEl: HTMLElement) => {
  if (typeof document === 'undefined') {
    return null;
  }
  const el = document.querySelector(`[data-ticket-id="${ticketId}"]`);
  if (!el || !boardEl) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  const boardRect = boardEl.getBoundingClientRect();
  return {
    left: rect.left - boardRect.left,
    top: rect.top - boardRect.top,
    width: rect.width,
    height: rect.height,
  };
};

const getColumnTargetPos = (colId: string, boardEl: HTMLElement) => {
  if (typeof document === 'undefined') {
    return null;
  }
  const colBody = document.querySelector(`[data-col="${colId}"] .colBody`);
  if (!colBody || !boardEl) {
    return null;
  }
  const rect = colBody.getBoundingClientRect();
  const boardRect = boardEl.getBoundingClientRect();
  return {
    left: rect.left - boardRect.left + rect.width / 2 - 80,
    top: rect.top - boardRect.top + 12,
  };
};

/* ── Sortable Ticket ── */
const SortableTicket = ({ ticket, justMoved }: { ticket: TicketData; justMoved: boolean }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ticket.id,
    data: { type: 'Ticket', ticket },
  });

  const role = ROLE_STYLES[ticket.tag];

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    ['--ticket-color' as string]: role.color,
    ['--ticket-soft' as string]: role.soft,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.ticket} ${ticket.tag === 'Security' ? styles.ticket_security : ''} ${justMoved ? styles.ticketEnter : ''}`}
      data-ticket-id={ticket.id}
      {...attributes}
      {...listeners}
    >
      <span className={styles.ticketAccent} />
      <div className={styles.ticketBody}>
        <div className={styles.ticketTop}>
          <span className={styles.ticketRole} style={{ color: role.color, background: role.soft }}>
            {role.icon}
            {ticket.tag}
          </span>
          <span className={styles.ticketGrip}>
            <SvgGrip />
          </span>
        </div>
        <p className={styles.ticketTitle}>{ticket.title}</p>
        {ticket.meta && (
          <span className={styles.ticketMeta}>
            {META_ICON[ticket.meta.type]}
            {ticket.meta.label}
          </span>
        )}
      </div>
    </div>
  );
};

/* ── Drag Overlay ── */
const OverlayTicket = ({ ticket }: { ticket: TicketData }) => {
  const role = ROLE_STYLES[ticket.tag];
  return (
    <div
      className={styles.ticket}
      style={{
        transform: 'rotate(2deg)',
        boxShadow: '0 16px 40px rgba(7,31,53,0.18)',
        cursor: 'grabbing',
        ['--ticket-color' as string]: role.color,
        ['--ticket-soft' as string]: role.soft,
      }}
    >
      <span className={styles.ticketAccent} />
      <div className={styles.ticketBody}>
        <div className={styles.ticketTop}>
          <span className={styles.ticketRole} style={{ color: role.color, background: role.soft }}>
            {role.icon}
            {ticket.tag}
          </span>
          <span className={styles.ticketGrip}>
            <SvgGrip />
          </span>
        </div>
        <p className={styles.ticketTitle}>{ticket.title}</p>
        {ticket.meta && (
          <span className={styles.ticketMeta}>
            {META_ICON[ticket.meta.type]}
            {ticket.meta.label}
          </span>
        )}
      </div>
    </div>
  );
};

/* ── Metric helpers ── */
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const formatCoverage = (n: number) => `${clamp(n, 0, 100).toFixed(1)}%`;

const getTestingMetrics = (
  testedCount: number,
  coverage: number,
  vuln: number
): TestingMetric[] => [
  {
    key: 'tested',
    label: 'Tickets en test',
    value: `${testedCount}/${TOTAL_TICKETS}`,
    sub: 'passés en recette',
    tone: 'neutral',
    icon: <SvgTicketStack />,
  },
  {
    key: 'coverage',
    label: 'Couverture code',
    value: formatCoverage(coverage),
    sub: 'globale · branches incluses',
    tone: coverage >= 95 ? 'good' : coverage >= 80 ? 'neutral' : 'warning',
    icon: <SvgPercent />,
  },
  {
    key: 'vulns',
    label: 'Vulnérabilités',
    value: vuln === 0 ? '0' : `${vuln}`,
    sub: vuln === 0 ? 'aucune détectée' : 'en cours de correction',
    tone: vuln === 0 ? 'good' : 'warning',
    icon: <SvgShield />,
  },
];

const getBuildingMetrics = () => [
  {
    key: 'commits',
    label: 'Commits auj.',
    value: '12',
    sub: 'sur branches actives',
    tone: 'neutral' as const,
    icon: <SvgGitBranch />,
  },
  {
    key: 'build',
    label: 'Build CI',
    value: 'Pass',
    sub: 'dernier run · 1m 24s',
    tone: 'good' as const,
    icon: <SvgCheckCircle />,
  },
  {
    key: 'deploy',
    label: 'Déploiements',
    value: '3',
    sub: 'env. de recette auj.',
    tone: 'neutral' as const,
    icon: <SvgCloud />,
  },
];

const getReviewMetrics = () => [
  {
    key: 'prs',
    label: 'PR ouvertes',
    value: '2',
    sub: 'en attente de review',
    tone: 'neutral' as const,
    icon: <SvgBox />,
  },
  {
    key: 'reviewTime',
    label: 'Temps moyen',
    value: '4h',
    sub: 'début à première revue',
    tone: 'good' as const,
    icon: <SvgClock />,
  },
  {
    key: 'approvals',
    label: 'Approbations',
    value: '5/5',
    sub: 'réalisées ce sprint',
    tone: 'good' as const,
    icon: <SvgCheckCircle />,
  },
];

const getDashboardMetrics = (coverage: number, vulnCount: number): DashboardMetric[] => [
  {
    key: 'dependabot',
    label: 'Dependabot',
    headline: '0 open',
    summary: 'Dépendances surveillées et PR automatiques vérifiées pendant le sprint.',
    status: 'ok',
    details: [
      { label: 'Security alerts', value: '0' },
      { label: 'Version drift', value: '0 critical' },
      { label: 'Auto-merge', value: 'ready' },
    ],
  },
  {
    key: 'codeguard',
    label: 'CodeGuard',
    headline: vulnCount === 0 ? '0 open' : '1 fixing',
    summary:
      vulnCount === 0
        ? 'Secrets, vulnérabilités et patterns risqués restent sous contrôle.'
        : 'Une alerte rouge est traitée immédiatement dans la colonne Review.',
    status: vulnCount === 0 ? 'ok' : 'warning',
    details: [
      { label: 'Secrets', value: '0' },
      { label: 'SAST', value: vulnCount === 0 ? 'clean' : 'patching' },
      { label: 'Time to repair', value: '2h' },
    ],
  },
  {
    key: 'sonar',
    label: 'Sonar',
    headline: 'Gate A',
    summary: 'Maintenabilité, duplication et couverture restent visibles à chaque ticket.',
    status: 'ok',
    details: [
      { label: 'Maintainability', value: 'A' },
      { label: 'Duplication', value: '1.1%' },
      { label: 'Coverage', value: formatCoverage(coverage) },
    ],
  },
];

const ToolLogo = ({ kind }: { kind: DashboardMetric['key'] }) => (
  <span className={`${styles.toolLogo} ${styles[`toolLogo_${kind}`]}`} aria-hidden="true">
    {kind === 'dependabot' && (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
        <rect x="5" y="7" width="14" height="11" rx="4" />
        <path d="M9 7V4h6v3" />
        <circle cx="10" cy="12.5" r="1" fill="currentColor" />
        <circle cx="14" cy="12.5" r="1" fill="currentColor" />
        <path d="M10 16h4" />
      </svg>
    )}
    {kind === 'codeguard' && <SvgShield />}
    {kind === 'sonar' && (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
        <path d="M4 15c3-5 8-8 16-8" />
        <path d="M7 18c2-3 5-5 10-6" />
        <path d="M11 20c1.5-1.5 3-2.4 6-3" />
      </svg>
    )}
  </span>
);

const QualityToolCard = ({ metric, index }: { metric: DashboardMetric; index: number }) => (
  <article
    className={`${styles.toolCard} ${styles[`toolCard_${metric.status}`]}`}
    data-dashboard-card={index}
  >
    <div className={styles.toolCardTop}>
      <ToolLogo kind={metric.key} />
      <div className={styles.toolNameGroup}>
        <span className={styles.toolName}>{metric.label}</span>
        <span className={styles.toolStatus}>scan temps réel</span>
      </div>
      <span className={styles.toolHeadline}>{metric.headline}</span>
    </div>
    <p className={styles.toolSummary}>{metric.summary}</p>
    <div className={styles.toolDetails}>
      {metric.details.map((detail) => (
        <span className={styles.toolDetail} key={`${metric.key}-${detail.label}`}>
          <span>{detail.label}</span>
          <strong>{detail.value}</strong>
        </span>
      ))}
    </div>
  </article>
);

/* ── Metric Card ── */
const MetricCard = ({ metric }: { metric: TestingMetric }) => {
  const tone = metric.tone;
  return (
    <div className={`${styles.metricCard} ${styles[`metricCard_${tone}`]}`}>
      <div className={styles.metricIcon}>{metric.icon}</div>
      <div className={styles.metricMain}>
        <span className={styles.metricValue}>{metric.value}</span>
        <span className={styles.metricLabel}>{metric.label}</span>
      </div>
      <span className={styles.metricSub}>{metric.sub}</span>
    </div>
  );
};

/* ── Inline Traveler (absolute inside board, follows scroll) ── */
const Traveler = ({ traveler }: { traveler: TravelerState }) => {
  const dx = traveler.toLeft - traveler.fromLeft;
  const dy = traveler.toTop - traveler.fromTop;
  const role = ROLE_STYLES[traveler.ticket.tag];

  return (
    <div
      className={styles.traveler}
      style={{
        left: traveler.fromLeft,
        top: traveler.fromTop,
        ['--dx' as string]: `${dx}px`,
        ['--dy' as string]: `${dy}px`,
        ['--ticket-color' as string]: role.color,
        ['--ticket-soft' as string]: role.soft,
      }}
    >
      <span className={styles.travelerAccent} style={{ background: role.color }} />
      <div className={styles.travelerBody}>
        <span className={styles.travelerRole} style={{ color: role.color, background: role.soft }}>
          {role.icon}
          {traveler.ticket.tag}
        </span>
        <span className={styles.travelerTitle}>{traveler.ticket.title}</span>
      </div>
    </div>
  );
};

/* ── Board ── */
interface ProjectBoardProps {
  showQualityDashboard?: boolean;
  showTicketBoard?: boolean;
  compact?: boolean;
  illustrativeLabel?: string;
  lang?: string;
}

const DEFAULT_ILLUSTRATIVE_LABEL = (lang: string) =>
  lang === 'en'
    ? 'Example project dashboard — illustrative data.'
    : 'Exemple de dashboard projet — données illustratives.';

export const ProjectBoard = ({
  showQualityDashboard = true,
  showTicketBoard = true,
  compact = false,
  illustrativeLabel,
  lang = 'fr',
}: ProjectBoardProps) => {
  const [tickets, setTickets] = useState<TicketData[]>(INITIAL_TICKETS);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [lastMovedId, setLastMovedId] = useState<string | null>(null);
  const [travelers, setTravelers] = useState<TravelerState[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [testedCount, setTestedCount] = useState(INITIAL_TESTED_COUNT);
  const [coverage, setCoverage] = useState(97.2);
  const [vulnCount, setVulnCount] = useState(0);
  const [incidentPhase, setIncidentPhase] = useState<IncidentPhase>('idle');
  const [dashboardMetricIndex, setDashboardMetricIndex] = useState(0);
  const testedRef = useRef(new Set<string>(INITIAL_TESTED_IDS));
  const securityLaunchedRef = useRef(false);
  const incidentTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const dashboardMetricsRef = useRef<HTMLDivElement | null>(null);

  /* IntersectionObserver: only animate when board is in viewport */
  useEffect(() => {
    const board = boardRef.current;
    if (!board) {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      threshold: 0.25,
    });
    observer.observe(board);
    return () => observer.disconnect();
  }, []);

  /* Mobile detection: disable drag on small viewports */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  /* Clear travelers */
  const clearTravelers = useCallback(() => setTravelers([]), []);

  useEffect(() => {
    if (travelers.length === 0) {
      return;
    }
    const t = setTimeout(clearTravelers, 1100);
    return () => clearTimeout(t);
  }, [travelers, clearTravelers]);

  useEffect(
    () => () => {
      incidentTimersRef.current.forEach(clearTimeout);
      incidentTimersRef.current = [];
    },
    []
  );

  /* Update testing metrics when a ticket reaches the Testing column */
  useEffect(() => {
    const inTesting = tickets
      .filter((t) => t.col === 'testing' && t.id !== SECURITY_TICKET_ID)
      .map((t) => t.id);
    let newlyTested = 0;
    inTesting.forEach((id) => {
      if (!testedRef.current.has(id)) {
        testedRef.current.add(id);
        newlyTested += 1;
      }
    });

    if (newlyTested > 0) {
      setTestedCount((c) => Math.min(c + newlyTested, TOTAL_TICKETS));
      setCoverage((prev) => {
        const delta = (Math.random() - 0.5) * 0.8;
        return clamp(parseFloat((prev + delta).toFixed(1)), 96.4, 98.6);
      });
    }
  }, [tickets]);

  /* Trigger inline traveler from ticket DOM to target column */
  const triggerTravel = useCallback((ticket: TicketData, nextCol: string, targetOffset = 0) => {
    const board = boardRef.current;
    if (!board) {
      return;
    }

    const from = getTicketRelativePos(ticket.id, board);
    if (!from) {
      return;
    }

    requestAnimationFrame(() => {
      const to = getColumnTargetPos(nextCol, board);
      if (!to) {
        return;
      }
      setTravelers((prev) => [
        ...prev,
        {
          ticket,
          fromLeft: from.left,
          fromTop: from.top,
          toLeft: to.left,
          toTop: to.top + targetOffset,
        },
      ]);
    });
  }, []);

  /* Surface a red security ticket when the testing score reaches 3/9 */
  useEffect(() => {
    if (!isVisible || testedCount < 3 || securityLaunchedRef.current) {
      return;
    }

    securityLaunchedRef.current = true;
    incidentTimersRef.current.forEach(clearTimeout);
    incidentTimersRef.current = [];

    const launchSecurityFix = () => {
      setTickets((prev) => {
        if (prev.some((ticket) => ticket.id === SECURITY_TICKET_ID)) {
          return prev;
        }
        return [...prev, SECURITY_TICKET];
      });
      setVulnCount(1);
      setIncidentPhase('detected');
      setLastMovedId(SECURITY_TICKET_ID);

      incidentTimersRef.current.push(
        setTimeout(() => {
          setIncidentPhase('repairing');
        }, 1400)
      );

      incidentTimersRef.current.push(
        setTimeout(() => {
          setTickets((prev) => {
            const current = prev.find((ticket) => ticket.id === SECURITY_TICKET_ID);
            if (!current || current.col === 'testing') {
              return prev;
            }

            triggerTravel(current, 'testing');
            return prev.map((ticket) =>
              ticket.id === SECURITY_TICKET_ID
                ? { ...ticket, col: 'testing', meta: { type: 'check', label: 'Correctif validé' } }
                : ticket
            );
          });
          setVulnCount(0);
          setIncidentPhase('resolved');
          setLastMovedId(SECURITY_TICKET_ID);
        }, 3200)
      );

      incidentTimersRef.current.push(
        setTimeout(() => {
          setTickets((prev) => prev.filter((ticket) => ticket.id !== SECURITY_TICKET_ID));
          setIncidentPhase('idle');
        }, 8200)
      );
    };

    const firstTimer = setTimeout(launchSecurityFix, 450);
    incidentTimersRef.current.push(firstTimer);
  }, [isVisible, testedCount, triggerTravel]);

  /* Auto-animate: let two Building tickets move together when the flow allows it */
  useEffect(() => {
    if (activeId || !isVisible || testedCount >= TOTAL_TICKETS) {
      return;
    }

    const interval = setInterval(() => {
      setTickets((prev) => {
        const movable = prev.filter((t) => t.col !== 'testing' && t.id !== SECURITY_TICKET_ID);
        if (movable.length === 0) {
          return prev;
        }

        const colIds = COLUMNS.map((c) => c.id);
        const picks: { ticket: TicketData; idx: number; nextCol: string }[] = [];

        const shuffled = [...movable].sort(() => Math.random() - 0.5);
        const buildingTickets = shuffled.filter((ticket) => ticket.col === 'building').slice(0, 2);
        const candidateTickets = buildingTickets.length >= 2 ? buildingTickets : shuffled;

        for (const ticket of candidateTickets) {
          const currentColIdx = colIds.indexOf(ticket.col);
          const nextColIdx = Math.min(currentColIdx + 1, colIds.length - 1);
          const nextCol = colIds[nextColIdx];

          if (ticket.col === nextCol) {
            continue;
          }

          const idx = prev.findIndex((t) => t.id === ticket.id);
          if (idx === -1) {
            continue;
          }

          picks.push({ ticket, idx, nextCol });

          if (picks.length >= 2) {
            break;
          }
        }

        if (picks.length === 0) {
          return prev;
        }

        const next = [...prev];
        picks.forEach((pick, pickIndex) => {
          triggerTravel(pick.ticket, pick.nextCol, pickIndex * 42);
          next[pick.idx] = { ...next[pick.idx], col: pick.nextCol };
          setLastMovedId(pick.ticket.id);
        });
        return arrayMove(next, 0, 0);
      });
    }, 1200);

    return () => clearInterval(interval);
  }, [activeId, isVisible, testedCount, triggerTravel]);

  /* Clear last-moved highlight */
  useEffect(() => {
    if (!lastMovedId) {
      return;
    }
    const t = setTimeout(() => setLastMovedId(null), 800);
    return () => clearTimeout(t);
  }, [lastMovedId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: isMobile ? { distance: 999 } : { distance: 5 },
    })
  );

  const ticketIds = useMemo(() => tickets.map((t) => t.id), [tickets]);
  const activeTicket = useMemo(
    () => tickets.find((t) => t.id === activeId) || null,
    [activeId, tickets]
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) {
      return;
    }

    const activeTicketId = active.id as string;
    const overId = over.id as string;

    const dragged = tickets.find((t) => t.id === activeTicketId);
    if (!dragged) {
      return;
    }

    // Dropped on column header
    const overColumn = COLUMNS.find((c) => c.id === overId);
    if (overColumn && dragged.col !== overColumn.id) {
      triggerTravel(dragged, overColumn.id);
      setTickets((prev) =>
        prev.map((t) => (t.id === activeTicketId ? { ...t, col: overColumn.id } : t))
      );
      setLastMovedId(activeTicketId);
      return;
    }

    // Dropped on another ticket
    const overTicket = tickets.find((t) => t.id === overId);
    if (!overTicket) {
      return;
    }

    const activeIdx = tickets.findIndex((t) => t.id === activeTicketId);
    const overIdx = tickets.findIndex((t) => t.id === overId);
    if (activeIdx === -1 || overIdx === -1) {
      return;
    }

    if (tickets[activeIdx].col !== tickets[overIdx].col) {
      triggerTravel(dragged, tickets[overIdx].col);
    }

    if (tickets[activeIdx].col === tickets[overIdx].col) {
      setTickets((prev) => arrayMove(prev, activeIdx, overIdx));
    } else {
      setTickets((prev) => {
        const next = [...prev];
        next[activeIdx] = { ...next[activeIdx], col: next[overIdx].col };
        return arrayMove(next, activeIdx, overIdx);
      });
      setLastMovedId(activeTicketId);
    }
  };

  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: { active: { opacity: '0.4' } },
    }),
  };

  const testingMetrics = getTestingMetrics(testedCount, coverage, vulnCount);
  const dashboard = useMemo(() => getDashboardMetrics(coverage, vulnCount), [coverage, vulnCount]);
  const resolvedIllustrativeLabel = illustrativeLabel ?? DEFAULT_ILLUSTRATIVE_LABEL(lang);

  const scrollDashboardMetricTo = useCallback((index: number) => {
    const metrics = dashboardMetricsRef.current;
    if (!metrics) {
      return;
    }

    const cards = Array.from(metrics.querySelectorAll<HTMLElement>('[data-dashboard-card]'));
    if (cards.length === 0) {
      return;
    }

    const nextIndex = (index + cards.length) % cards.length;
    const card = cards[nextIndex];

    metrics.scrollTo({ left: card.offsetLeft - metrics.offsetLeft, behavior: 'smooth' });
    setDashboardMetricIndex(nextIndex);
  }, []);

  const scrollDashboardMetrics = useCallback(
    (direction: 'left' | 'right') => {
      if (dashboard.length === 0) {
        return;
      }
      scrollDashboardMetricTo(dashboardMetricIndex + (direction === 'left' ? -1 : 1));
    },
    [dashboard.length, dashboardMetricIndex, scrollDashboardMetricTo]
  );

  const handleDashboardMetricsScroll = useCallback(() => {
    const metrics = dashboardMetricsRef.current;
    if (!metrics) {
      return;
    }

    const cards = Array.from(metrics.querySelectorAll<HTMLElement>('[data-dashboard-card]'));
    if (cards.length === 0) {
      return;
    }

    const viewportStart = metrics.scrollLeft;
    const nextIndex = cards.reduce((closestIndex, card, index) => {
      const closestCard = cards[closestIndex];

      return Math.abs(card.offsetLeft - viewportStart) <
        Math.abs(closestCard.offsetLeft - viewportStart)
        ? index
        : closestIndex;
    }, 0);

    setDashboardMetricIndex((current) => (current === nextIndex ? current : nextIndex));
  }, []);

  return (
    <div className={styles.wrapper} data-project-board>
      <div className={styles.boardBackground}>
        {showQualityDashboard && (
          <div className={styles.dashboardLine}>
            <div className={styles.dashboardLineInner}>
              <div className={styles.dashboardTitleGroup}>
                <span className={styles.dashboardTitle}>Qualité suivie en temps réel</span>
                <span className={styles.dashboardProof}>
                  Tests, sécurité, maintenabilité et couverture visibles pendant tout le
                  développement.
                </span>
              </div>
              <div
                className={styles.dashboardMetrics}
                ref={dashboardMetricsRef}
                onScroll={handleDashboardMetricsScroll}
              >
                {dashboard.map((metric, index) => (
                  <QualityToolCard key={metric.key} metric={metric} index={index} />
                ))}
              </div>
              <div className={styles.dashboardCarouselControls}>
                <button
                  type="button"
                  className={styles.dashboardScrollButton}
                  onClick={() => scrollDashboardMetrics('left')}
                  aria-label="Métrique qualité précédente"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className={styles.dashboardMetricDots} aria-label="Choisir un outil qualité">
                  {dashboard.map((metric, index) => (
                    <button
                      key={`${metric.key}-dot`}
                      type="button"
                      className={`${styles.dashboardMetricDot} ${
                        index === dashboardMetricIndex ? styles.dashboardMetricDotActive : ''
                      }`}
                      onClick={() => scrollDashboardMetricTo(index)}
                      aria-label={`Voir ${metric.label}`}
                      aria-current={index === dashboardMetricIndex ? 'true' : undefined}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.dashboardScrollButton}
                  onClick={() => scrollDashboardMetrics('right')}
                  aria-label="Métrique qualité suivante"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
              {resolvedIllustrativeLabel && (
                <p className={styles.illustrativeCaption}>{resolvedIllustrativeLabel}</p>
              )}
            </div>
          </div>
        )}

        {showTicketBoard && (
          <>
            <div className={styles.boardHeader}>
              <span className={styles.boardLabel}>
                <SvgGithub />
                Jira board · Sprint 4
              </span>
              <span className={styles.boardSprint}>24/32 tâches · 2j restants</span>
            </div>

            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <div
                className={`${styles.board} ${compact ? styles.boardCompact : ''}`}
                ref={boardRef}
              >
                <div
                  className={`${styles.bugCard} ${styles[`bugCard_${incidentPhase}`]}`}
                  aria-live="polite"
                >
                  <span className={styles.bugCardIcon}>
                    {incidentPhase === 'resolved' ? <SvgCheckCircle /> : <SvgBug />}
                  </span>
                  <span className={styles.bugCardBody}>
                    <span className={styles.bugCardLabel}>
                      {incidentPhase === 'resolved' ? 'Bugs résolus' : 'Bugs à fixer'}
                    </span>
                    <span className={styles.bugCardValue} key={`bug-${incidentPhase}`}>
                      {incidentPhase === 'detected' || incidentPhase === 'repairing'
                        ? '1 / 1'
                        : incidentPhase === 'resolved'
                          ? '0'
                          : '0 / 1'}
                    </span>
                  </span>
                  {incidentPhase === 'resolved' && (
                    <span className={styles.bugCardSub}>Time to repair 2h</span>
                  )}
                </div>
                {COLUMNS.map((col) => {
                  const colTickets = tickets.filter((t) => t.col === col.id);
                  const securityTicket = colTickets.find(
                    (ticket) => ticket.id === SECURITY_TICKET_ID
                  );
                  const regularTickets = colTickets.filter(
                    (ticket) => ticket.id !== SECURITY_TICKET_ID
                  );
                  const sortedColTickets: TicketData[] =
                    col.id === 'review' && securityTicket
                      ? regularTickets.length > 0
                        ? [regularTickets[0], securityTicket, ...regularTickets.slice(1)]
                        : [securityTicket]
                      : regularTickets;
                  const isTesting = col.id === 'testing';
                  const ticketLimit = compact ? 2 : 3;
                  const displayTickets = sortedColTickets.slice(0, ticketLimit);
                  const hiddenCount = Math.max(0, sortedColTickets.length - ticketLimit);
                  const isEmpty = colTickets.length === 0;
                  const colCountLabel = isTesting
                    ? `${testedCount}/${TOTAL_TICKETS}`
                    : colTickets.length;

                  return (
                    <div
                      key={col.id}
                      className={`${styles.column} ${isTesting ? styles.column_testing : ''} ${
                        compact ? styles.columnCompact : ''
                      }`}
                      data-col={col.id}
                    >
                      <div className={styles.colHeader} data-col-header>
                        <span className={styles.colLabel}>{col.label}</span>
                        <span className={styles.colCount}>{colCountLabel}</span>
                      </div>

                      <SortableContext items={ticketIds} strategy={verticalListSortingStrategy}>
                        <div className={styles.colBody}>
                          {isTesting ? (
                            <div className={`${styles.metricGrid} ${styles.testingMetricGrid}`}>
                              {testingMetrics.map((metric) => (
                                <MetricCard key={metric.key} metric={metric} />
                              ))}
                            </div>
                          ) : (
                            <>
                              {displayTickets.map((ticket) => (
                                <SortableTicket
                                  key={ticket.id}
                                  ticket={ticket}
                                  justMoved={lastMovedId === ticket.id}
                                />
                              ))}
                              {hiddenCount > 0 && (
                                <div className={styles.moreBadge}>+{hiddenCount} autres</div>
                              )}
                            </>
                          )}
                          {isEmpty && !isTesting && EMPTY_MESSAGES[col.id] && (
                            <>
                              <div className={styles.emptyState}>
                                <p>{EMPTY_MESSAGES[col.id]}</p>
                              </div>
                              <div className={styles.metricGrid}>
                                {(col.id === 'building'
                                  ? getBuildingMetrics()
                                  : getReviewMetrics()
                                ).map((metric) => (
                                  <MetricCard key={metric.key} metric={metric} />
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </SortableContext>
                    </div>
                  );
                })}

                {travelers.map((t, i) => (
                  <Traveler key={`${t.ticket.id}-${i}`} traveler={t} />
                ))}
              </div>

              <DragOverlay dropAnimation={dropAnimation}>
                {activeTicket ? <OverlayTicket ticket={activeTicket} /> : null}
              </DragOverlay>
            </DndContext>
          </>
        )}
      </div>
    </div>
  );
};
